import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';

const RecentDogsGallery = ({ initialLimit = 12 }) => {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [imageStatus, setImageStatus] = useState({});
  const batchSize = 8;

  // Function to preload images for smoother experience
  const preloadImages = (urls) => {
    urls.forEach(url => {
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  };

  const fetchRecentDogs = async (isInitial = false) => {
    try {
      const limit = isInitial ? initialLimit : batchSize;
      const offset = isInitial ? 0 : dogs.length;
      
      setLoading(isInitial);
      setLoadingMore(!isInitial);
      
      console.log(`Fetching dogs with limit=${limit} and offset=${offset}`);
      const response = await axios.get(`/api/analysis/gallery/images?limit=${limit}&offset=${offset}`);
      
      if (response.data.success) {
        const newDogs = response.data.data;
        console.log(`Received ${newDogs.length} new dogs`);
        
        // Add unique ID and fallback flag to each dog
        const dogsWithIds = newDogs.map((dog, idx) => ({
          ...dog,
          uniqueId: `dog-${offset + idx}-${Date.now()}`,
          imageLoaded: false,
          imageError: false
        }));
        
        // Preload images to reduce loading issues
        preloadImages(dogsWithIds.map(dog => dog.imageUrl));
        
        if (newDogs.length === 0 || newDogs.length < limit) {
          setAllLoaded(true);
        }
        
        if (isInitial) {
          setDogs(dogsWithIds);
        } else {
          setDogs(prev => [...prev, ...dogsWithIds]);
        }
        
        setError(null);
      } else {
        setError('No se pudieron cargar las imágenes de mascotas analizadas.');
      }
    } catch (err) {
      console.error('Error fetching dogs:', err);
      setError('Error al cargar las imágenes de mascotas analizadas.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleImageLoad = (dogId) => {
    setImageStatus(prev => ({
      ...prev,
      [dogId]: { loaded: true, error: false }
    }));
  };

  const handleImageError = (dogId) => {
    setImageStatus(prev => ({
      ...prev,
      [dogId]: { loaded: true, error: true }
    }));
    console.warn(`Image failed to load for dog ${dogId}`);
  };

  const handleDogClick = (dog) => {
    if (!dog.id) {
      console.log('⚠️ No ID available for this dog');
      return;
    }
    
    
    // Copy ID to clipboard for convenience
    navigator.clipboard.writeText(dog.id)
      .then(() => console.log('✅ ID copied to clipboard'))
      .catch(err => console.error('❌ Could not copy ID', err));
  };

  const loadMoreDogs = () => {
    setPage(prev => prev + 1);
    fetchRecentDogs(false);
  };

  useEffect(() => {
    fetchRecentDogs(true);
  }, [initialLimit]);

  // Loading state with skeleton placeholders
  if (loading && dogs.length === 0) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
        {[...Array(initialLimit)].map((_, i) => (
          <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && dogs.length === 0) {
    return (
      <div className="text-center text-amber-800 py-4">
        {error}
      </div>
    );
  }

  // Additional fallback images for variety
  const fallbackImages = [
    "/perro1.jpeg",
    "/demo-dogs/golden.jpg",
    "/demo-dogs/husky.jpg",
    "/demo-dogs/labrador.jpg",
    "/demo-dogs/poodle.jpg",
  ];

  const getFallbackImage = (index) => {
    return fallbackImages[index % fallbackImages.length];
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
        <AnimatePresence mode="sync">
          {dogs.map((dog, index) => (
            <motion.div 
              key={dog.uniqueId} 
              className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-200 cursor-pointer"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: Math.min(index * 0.05, 1) }}
              whileHover={{ y: -5 }}
              onClick={() => handleDogClick(dog)}
              title={`Click to log ID: ${dog.id}`}
            >
              {/* Dog Image with loading state */}
              <div className="aspect-square overflow-hidden bg-gray-100 relative">
                {/* Skeleton loader that shows until image loads */}
                {(!imageStatus[dog.uniqueId]?.loaded) && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                )}
                
                <img 
                  src={dog.imageUrl} 
                  alt={`${dog.breed} analizado`}
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${!imageStatus[dog.uniqueId]?.loaded ? 'opacity-0' : 'opacity-100'}`}
                  onLoad={() => handleImageLoad(dog.uniqueId)}
                  onError={(e) => {
                    handleImageError(dog.uniqueId);
                    e.target.src = getFallbackImage(index);
                    e.target.onerror = null; // Prevent infinite error loops
                  }}
                />
              </div>
              
              {/* Hover info overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <div className="text-white font-medium text-sm sm:text-base">{dog.breed}</div>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-block w-2 h-2 rounded-full bg-${dog.health.color}-500`}></span>
                  <span className="text-white text-xs">{dog.health.status}</span>
                </div>
              </div>
              
              {/* Health status indicator */}
              <div className={`absolute top-3 right-3 w-3 h-3 rounded-full bg-${dog.health.color}-500 shadow-md`}>
                <motion.div 
                  className={`absolute inset-0 rounded-full bg-${dog.health.color}-500`}
                  animate={{ scale: [1, 1.5, 1], opacity: [0.7, 0, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      
      {/* Load More Button */}
      {!allLoaded && dogs.length > 0 && (
        <div className="mt-8 text-center">
          <Button
            onClick={loadMoreDogs}
            disabled={loadingMore}
            variant="outline"
            className="rounded-full border border-amber-200 hover:bg-amber-50 px-5 py-2 transition-all duration-300"
          >
            {loadingMore ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Cargando...
              </span>
            ) : (
              <span className="flex items-center">
                Mostrar más
                <ChevronDown className="ml-2 h-4 w-4" />
              </span>
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default RecentDogsGallery; 